import { useState, useEffect, useContext } from 'react';
import {
  Table,
  Container,
  Button,
  SpaceBetween,
  Header,
  Box,
  Spinner,
  ProgressBar,
  Cards,
  Icon,
  Modal,
  RadioGroup
} from '@cloudscape-design/components';

// models
import { documentTableItem } from '../models/interfaces';
// helpers
// dummy data
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DocumentContext } from '../context/documentContext';
import { UserContext } from '../context/authContext';

export const DocumentTableView = () => {
  const navigate = useNavigate();
  const {  addDocToKnowledgeBase } =
    useContext(DocumentContext);
  const { auth } = useContext(UserContext);
  // state
  const [selectedItems, setSelectedItems] = useState<documentTableItem[]>([]);
  const [allDocuments, setAllDocuments] = useState<documentTableItem[]>([]);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showDocTypeModal, setShowDocTypeModal] = useState<boolean>(false);
  const [docTypeValue, setDocTypeValue] = useState<string>('');
  const [kbLoading, setKBLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { deleteDocument } = useContext(DocumentContext);

  const getAllDocs = async () => {
    const docs = await axios.get(
      `${process.env.REACT_APP_API}/alldocuments`,
      auth
    );
    if (docs.data) {
      setAllDocuments(docs.data);
    }
  };

  const getDocContinous = (allDocumentsLocal: documentTableItem[]) => {
    const docList = allDocumentsLocal.map((ele) => ele.status);
    if (!docList.includes('Analyzing')) {
      return;
    } else {
      getAllDocs();
    }
  };

  useEffect(() => {
    getAllDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      getDocContinous(allDocuments);
    }, 15000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDocuments]);

  const handleDelete = async () => {
    const tempSelectedItems = selectedItems;
    if (selectedItems.length < 1) return;
    setDeleting(true);
    for (let i = 0; i < tempSelectedItems.length; i++) {
      const result = await deleteDocument(tempSelectedItems[i]);
      if (result.sentences) {
        const tempAllDocs = allDocuments;
        const restDocs = tempAllDocs.filter(
          (ele) => ele._id !== tempSelectedItems[i]._id
        );
        setAllDocuments(restDocs);
      }
    }
    setSelectedItems([]);
    setDeleting(false);
  };

  const handleAddToKB = async () => {
    setKBLoading(true);
    const result = await addDocToKnowledgeBase(
      selectedItems[0]._id,
      docTypeValue
    );
    if (result.status === 200) {
      setDocTypeValue('');
      setShowDocTypeModal(false);
      getAllDocs();
    } else {
      setErrorMessage('Error adding to KB');
    }
  };

  return (
    <>
      <Container
        header={
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {selectedItems.length > 0 && (
                  <Button onClick={() => handleDelete()} loading={deleting}>
                    Delete Selected
                  </Button>
                )}
                <Button
                  variant="primary"
                  disabled={!(selectedItems.length === 1)}
                  onClick={() =>
                    navigate(`/app/base?documentId=${selectedItems[0]._id}`)
                  }
                >
                  View Document
                </Button>
              </SpaceBetween>
            }
          >
            Analyzed Documents
          </Header>
        }
      >
        <div style={{ overflow: 'scroll' }}>
          <Table
            onSelectionChange={({ detail }) =>
              setSelectedItems(detail.selectedItems)
            }
            items={allDocuments}
            selectionType="single"
            columnDefinitions={[
              {
                id: 'title',
                header: 'Document Tile',
                cell: (item: documentTableItem) => item.documentFileName,
                sortingField: 'documentFileName'
              },
              {
                id: 'docStatus',
                header: 'Document Status',
                cell: (item: documentTableItem) => {
                  if (item.status === 'analyzed') {
                    return (
                      <SpaceBetween size="l" direction="horizontal">
                        <Box>Analysis Complete</Box>
                        <ProgressBar status="success" />
                      </SpaceBetween>
                    );
                  }
                  if (item.status === 'Analyzing') {
                    return (
                      <SpaceBetween size="l" direction="horizontal">
                        <Box>Analysing Document</Box>
                        <Spinner></Spinner>
                      </SpaceBetween>
                    );
                  }
                  if (item.status === 'Processing Error') {
                    return (
                      <SpaceBetween size="l" direction="horizontal">
                        <Box>Processing Error</Box>
                        <Icon name="status-negative"></Icon>
                      </SpaceBetween>
                    );
                  }
                },
                sortingField: 'status'
              },
              // {
              //   id: 'docType',
              //   header: 'Document Type',
              //   cell: (item: documentTableItem) => item.documentType,
              //   sortingField: 'documentType'
              // },
              // {
              //   id: 'folder',
              //   header: 'Knowledge Base',
              //   cell: (item: documentTableItem) => `${item.knowledge_base}`,
              //   sortingField: 'folder'
              // }
            ]}
            loadingText="Loading resources"
            empty={
              <Box
                margin={{ vertical: 'xs' }}
                textAlign="center"
                color="inherit"
              >
                <SpaceBetween size="m">
                  <b>No resources</b>
                  <Button onClick={() => navigate('/app/upload')}>
                    Upload document to start
                  </Button>
                </SpaceBetween>
              </Box>
            }
            selectedItems={selectedItems}
          />
        </div>
      </Container>
      <Modal
        visible={showDocTypeModal}
        header={<Header>Select Document Type</Header>}
        size="large"
        onDismiss={() => {
          setShowDocTypeModal(false);
        }}
      >
        <SpaceBetween size={'s'} direction="vertical">
          {!kbLoading ? (
            <>
              <RadioGroup
                onChange={({ detail }) => setDocTypeValue(detail.value)}
                value={docTypeValue}
                items={[
                  { value: 'Organizational', label: 'Organizational' },
                  { value: 'Customer Contract', label: 'Customer Contract' },
                  { value: 'Vendor Contract', label: 'Vendor Contract' }
                ]}
              />
              <Button
                disabled={docTypeValue === '' ? true : false}
                onClick={() => handleAddToKB()}
              >
                Add to Knowledge Base
              </Button>
            </>
          ) : (
            <Spinner />
          )}
          {errorMessage ? <Box>{errorMessage}</Box> : null}
        </SpaceBetween>
      </Modal>
    </>
  );
};
